import { useCallback, useEffect, useMemo } from 'react';

import { generatePath, useNavigate } from 'react-router-dom';
import { PaymentType, ROUTES, mapServerError } from 'core';
import { selectPreparationCentre } from 'store/country/countrySlice';
import { useAppSelector } from 'store';
import { usePayOfflineMutation, usePayOnlineMutation } from 'store/payment/paymentAPI';

import { ServerError } from '@britishcouncil/react-forms';

export const useSelectPaymentSubmit = () => {
  const navigate = useNavigate();
  const pcInfo = useAppSelector(selectPreparationCentre);
  const [payOffline, payOfflineData] = usePayOfflineMutation();
  const [payOnline, payOnlineData] = usePayOnlineMutation();

  const submit = useCallback(
    (data: { paymentType: PaymentType; referenceNumber: string }) => {
      if (data.paymentType === PaymentType.OfflinePayment) {
        payOffline({ ...data, preparationCentreName: pcInfo?.preparationCentreName });
      } else if (data.paymentType === PaymentType.OnlinePayment) {
        payOnline(data);
      }
    },
    [payOffline, payOnline, pcInfo?.preparationCentreName]
  );

  const serverErrors = useMemo<ServerError[]>(() => {
    let lastMutation = [payOfflineData, payOnlineData].sort(
      (x, y) => (y.startedTimeStamp ?? 0) - (x.startedTimeStamp ?? 0)
    )[0];

    if (!lastMutation.isError) return [];

    return mapServerError(lastMutation.error);
  }, [payOfflineData, payOnlineData]);

  useEffect(() => {
    if (payOnlineData.isSuccess) {
      window.location.replace(payOnlineData.data);
    }
  }, [payOnlineData]);

  useEffect(() => {
    if (payOfflineData.isSuccess) {
      navigate(
        generatePath(ROUTES.PAYMENT_OFFLINE_SUMMARY, {
          referenceNumber: payOfflineData.originalArgs?.referenceNumber ?? '',
        })
      );
    }
  }, [navigate, payOfflineData]);

  return {
    submit,
    serverErrors,
    isSubmitting: payOfflineData.isLoading || payOnlineData.isLoading,
  };
};
